<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Payment Channels" subTitle="Transactions" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
          <form class="position-relative table-src-form me-0" @submit.prevent>
            <input type="text" class="form-control" placeholder="Search channels..." v-model="searchQuery" />
            <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
          </form>
          <RouterLink to="/payment-channels/create" v-if="isSuperAdmin" class="btn btn-outline-primary py-1 px-2 px-sm-4 fs-14 fw-medium rounded-3 hover-bg">
            <span class="py-sm-1 d-block">
              <i class="ri-add-line d-none d-sm-inline-block me-1"></i>
              <span>Add New Channel</span>
            </span>
          </RouterLink>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">Type</th>
                  <th scope="col">Provider</th>
                  <th scope="col">Account Info</th>
                  <th scope="col">Status</th>
                  <th scope="col" v-if="isSuperAdmin">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="5" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="filteredChannels.length === 0">
                  <td colspan="5" class="text-center py-4">No payment channels found.</td>
                </tr>
                <tr v-else v-for="channel in filteredChannels" :key="channel.channel_id">
                  <td><span class="badge bg-secondary">{{ channel.channel_type }}</span></td>
                  <td>
                    <h6 class="mb-0 fs-14 fw-medium">{{ channel.provider_name }}</h6>
                  </td>
                  <td>
                    <div v-if="channel.account_number">{{ channel.account_number }}<br/><small class="text-muted">{{ channel.account_name }}</small></div>
                    <div v-if="channel.qr_image_file"><a :href="getImageUrl(channel.qr_image_file)" target="_blank" class="text-primary">View QR</a></div>
                  </td>
                  <td>
                    <span class="badge bg-opacity-10 p-2 fs-12 fw-normal" :class="channel.is_active ? 'bg-success text-success' : 'bg-danger text-danger'">
                      {{ channel.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td v-if="isSuperAdmin">
                    <div class="d-flex align-items-center gap-1">
                      <RouterLink :to="`/payment-channels/${channel.channel_id}/edit`" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-body">edit</i>
                      </RouterLink>
                      <button @click="deleteChannel(channel.channel_id)" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-danger">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import paymentChannelService from '@/services/paymentChannelService';

export default defineComponent({
  name: 'PaymentChannelListPage',
  components: {
    PageTitle
  },
  setup() {
    const channels = ref<any[]>([]);
    const loading = ref(true);
    const searchQuery = ref('');

    const isSuperAdmin = computed(() => {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      try {
        const user = JSON.parse(userStr);
        return user.role === 'super_admin';
      } catch (e) {
        return false;
      }
    });

    const fetchChannels = async () => {
      loading.value = true;
      try {
        const response = await paymentChannelService.getAll();
        channels.value = response.data;
      } catch (error) {
        console.error('Error fetching channels:', error);
      } finally {
        loading.value = false;
      }
    };

    const deleteChannel = async (id: number) => {
      if (confirm('Are you sure you want to delete this channel?')) {
        try {
          await paymentChannelService.delete(id);
          fetchChannels();
        } catch (error) {
          console.error('Error deleting channel:', error);
        }
      }
    };

    const filteredChannels = computed(() => {
      if (!searchQuery.value) return channels.value;
      const q = searchQuery.value.toLowerCase();
      return channels.value.filter(c => 
        c.provider_name.toLowerCase().includes(q) || 
        c.channel_type.toLowerCase().includes(q)
      );
    });

    onMounted(() => {
      fetchChannels();
    });

    const getImageUrl = (path: string) => {
      if (!path) return '';
      const baseUrl = process.env.NODE_ENV === 'production' 
        ? 'https://backend-beta.rayacheck.id' 
        : 'http://jurnal-backend.test';
      return `${baseUrl}/storage/${path}`;
    };

    return {
      channels,
      loading,
      searchQuery,
      filteredChannels,
      deleteChannel,
      isSuperAdmin,
      getImageUrl
    };
  }
});
</script>
